import { Box, Button, DialogActions, Drawer, FormControl, Grid, MenuItem, Select, styled, useMediaQuery } from "@mui/material";
import { useWorkItemContext, useWorkItemPermissions } from "hooks/work-items";
import { useEffect, useState } from "react";
import useWorkItemResourceAllocation from "hooks/work-items/useWorkItemResourceAllocation";
import useDeleteResourceFromResponsibility from "hooks/work-items/useDeleteResourceFromResponsibility";
import ResourceAllocationGrid from "./ResourceAllocationGrid";
import IWorkItemResourceAllocation from "models/work-items/IWorkItemResourceAllocation";
import useWorkItemResponsibilities from "hooks/work-items/useWorkItemResponsibilities";
import { DeleteDialog, DrawerContent, DrawerHeader, Ico_Plus } from "@sagecentral/core-ui";
import IWorkItemResponsibility from "models/work-items/IWorkItemResponsibility";
import useWorkItemDraftContext from "hooks/work-items/useWorkItemDraftContext";

const FormTitle = styled(Box)({
    fontSize: '22px',
    marginBottom: '10px',
    fontWeight: 'bold'
});

type ResourceAllocationProps = {
    draft?: boolean;
};

const ResourceAllocation = (props: ResourceAllocationProps) => {
    const { draft } = props;
    const draftContext = useWorkItemDraftContext();
    const { revalidate } = useWorkItemDraftContext();
    const workItemContext = useWorkItemContext();
    const workItemId = draft && draftContext.workItem?.id ? draftContext.workItem.id : workItemContext.workItem?.id;
    const workItemPermissions = useWorkItemPermissions();
    const [data, total, loading, error, refreshData] = useWorkItemResourceAllocation(workItemId ?? 0, revalidate, draft)
    const [dataState, setDataState] = useState<IWorkItemResourceAllocation[] | null>(data);
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const [resourceToDelete, setResourceToDelete] = useState<{ workItemId: number, resourceAllocationId: number }>({ workItemId: 0, resourceAllocationId: 0 });
    const handleDeleteResource = useDeleteResourceFromResponsibility();
    const [open, setOpen] = useState(false);
    const [responsibilityToBeAdded, setResponsibilityToBeAdded] = useState(0);
    const [responsibilityList, setResponsibilityList] = useState<IWorkItemResponsibility[] | null>([]);
    const isLargeScreen = useMediaQuery('(min-width:1600px)');

    const [fetchResponsibilities, addResponsibilities, refresh, loadingResponsibilities] = useWorkItemResponsibilities(workItemId ?? 0);

    useEffect(() => {
        setDataState(data)
    }, [data])

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setResponsibilityToBeAdded(0);
        setOpen(false)
    };
    const handleSave = async () => {
        await addResponsibilities(workItemId ?? 0, responsibilityToBeAdded, draft);
        refreshData();
        setResponsibilityToBeAdded(0);
        handleClose();
    };

    const handleDeleteConfirmation = async () => {
        await handleDeleteResource(resourceToDelete.workItemId, resourceToDelete.resourceAllocationId, draft);
        refreshData();
        setOpenDeleteConfirmation(false)
    }

    const handleFetchResponsibilities = async (event: React.MouseEvent<HTMLButtonElement>) => {
        if(event){
            event.currentTarget.blur();
        }
        var responsibilities = await fetchResponsibilities(workItemId ?? 0, draft);
        setResponsibilityList(responsibilities)
        handleOpen();
    }


    return (
        <>
        {
            draft &&
            <FormTitle>
                Resource Allocation
            </FormTitle>
        }
        {(workItemContext || draftContext) &&
            <Grid container>
                <Grid sx={{ fontSize: '14px', display: 'flex', mb: '24px' }} item xs={12} justifyContent="flex-start">
                    {workItemPermissions?.canAssignAdditionalResponsibilities &&
                        <Button startIcon={<Ico_Plus />} variant="outlined" sx={{ ml: '0', width: '212px', mr: '0' }} onClick={ (event) => handleFetchResponsibilities(event)}>Add Responsibility</Button>
                    }
                </Grid>

                <Grid item xs={12} sx={{width: '1100px'}}>
                    {dataState &&

                        <ResourceAllocationGrid
                            data={dataState}
                            loading={loading}
                            workItem={draft ? draftContext.workItem : workItemContext.workItem}
                            workItemPermissions={workItemPermissions}
                            draft={draft}
                            setResourceToDelete={setResourceToDelete}
                            setOpenDeleteConfirmation={setOpenDeleteConfirmation}
                            setDataState={setDataState}
                            refreshData={refreshData}/>
                    }
                </Grid>

                <DeleteDialog
                    nameOfItem="Resource"
                    onCancel={() => setOpenDeleteConfirmation(!openDeleteConfirmation)}
                    onDelete={() => { handleDeleteConfirmation() }}
                    open={openDeleteConfirmation}
                />

                <Drawer
                    open={open}
                    onClose={handleClose}
                    anchor="right"
                    PaperProps={{
                        sx: {
                            width: '30%',
                        }
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%'
                    }}>
                        <DrawerHeader
                            title='Add Responsibility'
                            onClose={handleClose}
                        />
                        <DrawerContent>
                            <FormControl fullWidth>
                                <Select
                                    value={responsibilityToBeAdded}
                                    onChange={e => setResponsibilityToBeAdded(+e.target.value)}
                                    fullWidth={true}
                                    sx={{
                                        width: '100%'

                                    }}
                                >
                                    <MenuItem value={0}>
                                        <em>Select a responsibility</em>
                                    </MenuItem>
                                    {responsibilityList?.map((responsibility, idx) => (
                                        <MenuItem key={idx} value={responsibility.id}>{responsibility.description}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <DialogActions sx={{ paddingTop: '20px', pr: 0, pl: 0 }}>
                                <Button sx={{ ml: 0 }} variant="outlined" fullWidth onClick={handleClose}>Cancel</Button>
                                <Button sx={{ mr: 0 }} variant="contained" fullWidth disabled={responsibilityToBeAdded === 0} onClick={handleSave}>Add</Button>
                            </DialogActions>
                        </DrawerContent>
                    </Box>
                </Drawer>

            </Grid>
        }
        </>
    );
}

export default ResourceAllocation;